import { useMemo, useState, type ReactElement } from 'react'
import type { Category } from './api/categoriesApi.ts'
import { CatalogSelector } from './components/CatalogSelector.tsx'
import { ConfirmationModal } from './components/ConfirmationModal.tsx'
import { SearchSpinner } from './components/SearchSpinner.tsx'
import { useCategorySearch } from './hooks/useCategorySearch.ts'
import { useDebouncedValue } from './hooks/useDebouncedValue.ts'
import type { SelectedCategory } from './types/category.ts'

export interface ConfirmationModalState {
    readonly isOpen: boolean
    readonly title: string
    readonly message: string
    readonly onConfirm: () => void
    readonly onCancel: () => void
}

interface AppProps {
    readonly selectedCategory: Prettify<SelectedCategory> | undefined
    readonly onCategoryToggle: (category: Prettify<Category>) => void
    readonly onCategoryRemove: (categoryId: string) => void
    readonly onClearAll: () => void
    readonly selectedCatalogId: string
    readonly onCatalogChange: (catalogId: string) => Promise<void>
    readonly errorMessage?: string
    readonly categorySelectionError?: string
    readonly confirmationModal?: ConfirmationModalState
}

const DEBOUNCE_DELAY_MS = 400 as const
const CATEGORY_RESULT_LIMIT = 100 as const

const EMPTY_RESULTS_MESSAGE = 'No categories found' as const

export function App({
    selectedCategory,
    onCategoryToggle,
    onCategoryRemove,
    onClearAll,
    selectedCatalogId,
    onCatalogChange,
    errorMessage,
    categorySelectionError,
    confirmationModal
}: AppProps): ReactElement {
    const [searchTerm, setSearchTerm] = useState('')
    const debouncedSearchTerm = useDebouncedValue(searchTerm, DEBOUNCE_DELAY_MS)
    const isDebouncing = debouncedSearchTerm !== searchTerm

    // Use the OCAPI category search hook with selected catalog
    const {
        categories,
        total,
        isLoading,
        error: searchError,
        retry
    } = useCategorySearch(selectedCatalogId, debouncedSearchTerm, CATEGORY_RESULT_LIMIT)

    // Combined loading state: debouncing or API request in flight
    const isSearching = isDebouncing || isLoading

    const resultSummary = useMemo(() => {
        if (searchError) {
            return 'Error loading categories'
        }

        if (!debouncedSearchTerm.trim()) {
            return `Showing the first ${categories.length} of ${total} categories`
        }

        if (categories.length === 0) {
            return `${EMPTY_RESULTS_MESSAGE} for "${debouncedSearchTerm}"`
        }

        if (categories.length < total) {
            return `Showing first ${categories.length} of ${total} result(s)`
        }

        return `Showing all ${categories.length} result(s)`
    }, [debouncedSearchTerm, categories.length, total, searchError])

    return (
        <main className='bg-slate-950 text-slate-100'>
            <div className='mx-auto flex max-w-2xl flex-col gap-4 px-6 py-10'>
                {errorMessage ? (
                    <div
                        role='alert'
                        className='rounded-md border border-rose-500/60 bg-rose-500/10 p-4 text-sm text-rose-100'
                    >
                        {errorMessage}
                    </div>
                ) : null}

                <section className='rounded-lg border border-slate-800 bg-slate-900 p-6 shadow-sm'>
                    <div className='flex flex-col gap-10'>
                        <div className='rounded-xl border border-sky-500/40 bg-sky-500/10 px-4 py-3 text-sm text-sky-200 shadow-[0_0_0_1px_rgba(14,165,233,0.15)]'>
                            <p className='flex items-center gap-5'>
                                <span aria-hidden className='text-lg'>
                                    ✨
                                </span>
                                Select a catalog first, then search and select one category from
                                SFCC. Click to select or deselect. Your selection is saved
                                automatically.
                            </p>
                        </div>

                        <CatalogSelector
                            selectedCatalogId={selectedCatalogId}
                            onCatalogChange={onCatalogChange}
                            disabled={isLoading}
                        />

                        {/* Selected Category Display */}
                        <div>
                            <div className='flex items-center gap-3'>
                                <label className='text-md font-medium text-white'>
                                    Selected Category
                                </label>
                                {selectedCategory ? (
                                    <button
                                        type='button'
                                        onClick={() => void onClearAll()}
                                        className='rounded-md bg-slate-700/50 px-3 py-1 text-xs font-medium text-slate-300 transition hover:bg-slate-700'
                                    >
                                        Clear All
                                    </button>
                                ) : null}
                            </div>
                            {selectedCategory ? (
                                <div className='mt-3 flex flex-wrap gap-2'>
                                    <div className='flex items-center gap-2 rounded-full border border-emerald-500/50 bg-emerald-500/10 py-1.5 pr-1.5 pl-3'>
                                        <span className='text-sm font-medium text-emerald-200'>
                                            {selectedCategory.name}
                                        </span>
                                        <button
                                            type='button'
                                            onClick={() =>
                                                void onCategoryRemove(selectedCategory.id)
                                            }
                                            aria-label={`Remove ${selectedCategory.name}`}
                                            className='flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-200 transition hover:bg-emerald-500/40'
                                        >
                                            <svg
                                                xmlns='http://www.w3.org/2000/svg'
                                                className='h-3 w-3'
                                                viewBox='0 0 20 20'
                                                fill='currentColor'
                                            >
                                                <path
                                                    fillRule='evenodd'
                                                    d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                                                    clipRule='evenodd'
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <p className='mt-3 text-sm text-slate-400'>
                                    No category selected. Search and select a category below.
                                </p>
                            )}
                        </div>

                        <div className='flex flex-col gap-5'>
                            <div className='relative'>
                                <label
                                    className='text-md font-medium text-white'
                                    htmlFor='category-search'
                                >
                                    Search Categories
                                </label>
                                <input
                                    id='category-search'
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    placeholder='Search categories'
                                    className='mt-3 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-base text-slate-100 placeholder:text-slate-500 focus:border-sky-500 focus:ring-2 focus:ring-sky-600 focus:outline-none'
                                />
                                {searchTerm.length > 0 && !isSearching ? (
                                    <button
                                        type='button'
                                        aria-label='Clear search'
                                        onClick={() => setSearchTerm('')}
                                        className='absolute inset-y-0 right-3 flex items-center text-slate-400 transition hover:text-slate-200'
                                    >
                                        <svg
                                            xmlns='http://www.w3.org/2000/svg'
                                            className='h-4 w-4'
                                            viewBox='0 0 20 20'
                                            fill='currentColor'
                                        >
                                            <path
                                                fillRule='evenodd'
                                                d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                                                clipRule='evenodd'
                                            />
                                        </svg>
                                    </button>
                                ) : null}
                                {isSearching ? <SearchSpinner /> : null}
                            </div>

                            {searchError ? (
                                <div
                                    role='alert'
                                    className='rounded-md border border-rose-500/60 bg-rose-500/10 p-4 text-sm text-rose-100'
                                >
                                    <div className='flex items-center justify-between'>
                                        <span>Failed to load categories: {searchError}</span>
                                        <button
                                            type='button'
                                            onClick={retry}
                                            className='rounded-md bg-rose-500/20 px-3 py-1 text-xs font-medium text-rose-200 transition hover:bg-rose-500/30'
                                        >
                                            Retry
                                        </button>
                                    </div>
                                </div>
                            ) : null}

                            {categorySelectionError ? (
                                <div
                                    role='alert'
                                    className='rounded-md border border-rose-500/60 bg-rose-500/10 p-4 text-sm text-rose-100'
                                >
                                    {categorySelectionError}
                                </div>
                            ) : null}

                            <div className='relative rounded-md border border-slate-800 bg-slate-950'>
                                <header className='flex items-center justify-between border-b border-slate-800 px-4 py-2 text-xs text-slate-400'>
                                    <span>{resultSummary}</span>
                                    <span>{total} total</span>
                                </header>
                                <div className='relative'>
                                    <ul className='m-0 max-h-72 list-none divide-y divide-slate-800 overflow-y-auto p-0'>
                                        {categories.map((category) => {
                                            const isSelected = selectedCategory?.id === category.id
                                            return (
                                                <li key={category.id}>
                                                    <button
                                                        type='button'
                                                        aria-pressed={isSelected}
                                                        onClick={() =>
                                                            void onCategoryToggle(category)
                                                        }
                                                        className={`flex w-full items-center justify-between px-4 py-3 text-left text-sm transition ${
                                                            isSelected
                                                                ? 'bg-emerald-500/20 text-emerald-200'
                                                                : 'bg-sky-500/15 text-sky-100 hover:bg-slate-900/80 hover:text-slate-200'
                                                        }`}
                                                    >
                                                        <div className='flex items-center gap-3'>
                                                            <span
                                                                className={`flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                                                                    isSelected
                                                                        ? 'border-emerald-500'
                                                                        : 'border-slate-600'
                                                                }`}
                                                            >
                                                                {isSelected ? (
                                                                    <span className='h-2 w-2 rounded-full bg-emerald-500' />
                                                                ) : null}
                                                            </span>
                                                            <span className='font-medium'>
                                                                {category.name ?? category.id}
                                                            </span>
                                                        </div>
                                                        <span
                                                            className={`font-mono text-xs ${isSelected ? 'text-emerald-300/70' : 'text-slate-400'}`}
                                                        >
                                                            #{category.id}
                                                        </span>
                                                    </button>
                                                </li>
                                            )
                                        })}
                                        {categories.length === 0 && !isSearching ? (
                                            <li className='px-4 py-3 text-sm text-slate-400'>
                                                No matches found
                                            </li>
                                        ) : null}
                                    </ul>
                                    {isSearching ? <SearchSpinner overlay size='lg' /> : null}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {confirmationModal?.isOpen ? (
                <ConfirmationModal
                    isOpen={confirmationModal.isOpen}
                    title={confirmationModal.title}
                    message={confirmationModal.message}
                    onConfirm={confirmationModal.onConfirm}
                    onCancel={confirmationModal.onCancel}
                />
            ) : null}
        </main>
    )
}

export default App
